/* eslint-disable no-param-reassign */
/*
 ** 查房 & 查房结果 抽屉
 */
import React, { useState, useCallback, useEffect, useReducer } from 'react';
import { Spin, Button, Table, Input, Popconfirm, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';

import usePortalDrawer, { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import PortalLoading from '@/components/PortalLoading';

import { getInventoryDetail, saveInventoryResult, IComputerList } from '@/api/inventory';
import { parseYuan2Currency } from '@/utils/number';
import { CheckType } from '@/config/constant';

import { initState, inventoryReducer } from './reducer';
import {
  getComputerColumns,
  getGoodsConsumColumns,
  getTypeText,
  calGoodsTotalConsum,
  IChangeConsumeQty,
  getPheripheralReturnColumns,
} from './help';

import styles from './index.module.scss';

/**
 * @param IConfig 参数
 * @example inventoryPortal.showDrawer({ id, status, callback });
 */
interface IConfig {
  resvNo: string; // 归属订单
  id: bigint; // 查房的主键id
  status: 1 | 2; // 查房状态【1待查房 2已查房】
  callback?: () => void; // 确认按钮的回调函数  此处最好是查房列表的数据重新获取
}

interface IProps extends IConfig {
  unmountNode: () => void;
}

const Index: React.FC<IConfig> = ($$: IProps) => {
  const [modalState, setModalState] = useState({ isInit: false, isLoading: false });

  const [$, dispatch] = useReducer(inventoryReducer, initState);

  // 是否是查房结果
  const isResult = $$.status === 2;

  const updateModalState = useCallback<(state: Partial<typeof modalState>) => void>((state) => {
    setModalState((prevState) => ({ ...prevState, ...state }));
  }, []);

  // init
  const init = async () => {
    try {
      dispatch({ type: 'update-state', payload: { pageLoading: true } });
      const { code, data } = await getInventoryDetail($$.id);
      if (code === 0) {
        // 查房检测默认正常
        const computerList = data.computerList
          ? data.computerList.map((item: IComputerList) => {
              if (item.manualCheck === null) {
                // eslint-disable-next-line no-param-reassign
                item.manualCheck = 1;
              }
              return item;
            })
          : [];
        dispatch({
          type: 'init',
          payload: {
            type: data.type || null,
            roomNo: data.roomNo || '',
            billRoomNo: data.billRoomNo || '',
            computerList,
            goodsList: data.goodsList || [],
            peripheralList: data.peripheralList || [],
          },
        });
      }
      dispatch({ type: 'update-state', payload: { pageLoading: false } });
    } catch (error) {
      dispatch({ type: 'update-state', payload: { pageLoading: false } });
    }
  };

  // 查房检测事件
  const manualCheckChange = (params: { computerId: bigint; manualCheck: 1 | 2 | null }) => {
    const computerList = $.computerList
      ? $.computerList.map((item: IComputerList) => {
          if (item.computerId === params.computerId) {
            // eslint-disable-next-line no-param-reassign
            item.manualCheck = params.manualCheck;
          }
          return item;
        })
      : [];
    dispatch({ type: 'update-state', payload: { computerList } });
  };

  // 商品剩余与本次消耗事件
  const changeConsumeQty = ({ type, skuCode, consumeQty }: IChangeConsumeQty) => {
    const goodsList = $.goodsList || [];
    switch (type) {
      case 'add':
        const newGoodList1 = goodsList.map((item) => {
          if (item.skuCode === skuCode) {
            // 达到最大值 消耗大于等于上次剩余
            if (item.consumeQty >= item.lastRemainQty) {
              message.warning(intl.get('cmp_inventory_error_value_max'));
              return item;
            }
            item.remainQty -= 1;
            item.consumeQty += 1;
          }
          return item;
        });
        dispatch({ type: 'update-state', payload: { goodsList: newGoodList1 } });
        break;
      case 'reduce':
        const newGoodList2 = goodsList.map((item) => {
          if (item.skuCode === skuCode) {
            // 达到最小值 剩余数量为0
            if (item.consumeQty === 0) {
              message.warning(intl.get('cmp_inventory_error_value_min'));
              return item;
            }
            item.remainQty += 1;
            item.consumeQty -= 1;
          }
          return item;
        });
        dispatch({ type: 'update-state', payload: { goodsList: newGoodList2 } });
        break;
      case 'change':
        const newGoodList3 = goodsList.map((item) => {
          if (item.skuCode === skuCode) {
            // 达到最大值 上次剩余不为空 消耗数量大于上次剩余
            if (item.lastRemainQty != null && consumeQty > item.lastRemainQty) {
              message.warning(intl.get('cmp_inventory_error_value_invalid'));
              return item;
            }
            item.remainQty = item.lastRemainQty - consumeQty;
            item.consumeQty = consumeQty;
          }
          return item;
        });
        dispatch({ type: 'update-state', payload: { goodsList: newGoodList3 } });
        break;
      default:
        break;
    }
  };

  // 外设剩余与本次消耗事件
  const changePeripheralConsumeQty = ({ type, skuCode, consumeQty }: IChangeConsumeQty) => {
    const peripheralList = $.peripheralList || [];
    switch (type) {
      case 'add':
        const newList1 = peripheralList.map((item) => {
          if (item.skuCode === skuCode) {
            // 达到最大值 消耗大于等于上次剩余
            if (item.returnQty >= item.qty) {
              message.warning(intl.get('cmp_inventory_error_value_max'));
              return item;
            }
            item.returnQty += 1;
          }
          return item;
        });
        dispatch({ type: 'update-state', payload: { peripheralList: newList1 } });
        break;
      case 'reduce':
        const newList2 = peripheralList.map((item) => {
          if (item.skuCode === skuCode) {
            // 达到最小值 剩余数量为0
            if (item.returnQty === 0) {
              message.warning(intl.get('cmp_inventory_error_value_min'));
              return item;
            }
            item.returnQty -= 1;
          }
          return item;
        });
        dispatch({ type: 'update-state', payload: { peripheralList: newList2 } });
        break;
      case 'change':
        const newList3 = peripheralList.map((item) => {
          if (item.skuCode === skuCode) {
            // 达到最大值 上次剩余不为空 消耗数量大于上次剩余
            if (consumeQty > item.qty) {
              message.warning(intl.get('cmp_inventory_error_value_invalid'));
              return item;
            }
            item.returnQty = consumeQty;
          }
          return item;
        });
        dispatch({ type: 'update-state', payload: { peripheralList: newList3 } });
        break;
      default:
        break;
    }
  };

  // 文本输入
  const onInput = (e: any) => {
    dispatch({ type: 'update-state', payload: { searchText: e.target.value } });
  };

  // 搜索
  const searchHandle = () => {
    if (!$.searchText || $.searchText.length === 0) {
      dispatch({ type: 'update-state', payload: { searching: false } });
      return;
    }
    dispatch({ type: 'update-state', payload: { searching: true } });
    const searchText = $.searchText.trim();
    if ($.goodsList && $.goodsList.length > 0) {
      const goodsListCopy = $.goodsList.filter((item) => {
        return item.spuName.includes(searchText) || item.skuName.includes(searchText);
      });
      dispatch({ type: 'update-state', payload: { goodsListCopy } });
    }
  };

  // 重置
  const resetHandle = () => {
    dispatch({
      type: 'update-state',
      payload: { searchText: '', goodsListCopy: $.goodsList, searching: false },
    });
  };

  // mounted
  useEffect(() => {
    updateModalState({ isInit: true });
    init();
  }, []);

  // 表格列
  const computerColumns = getComputerColumns(manualCheckChange, isResult);
  const goodsConsumColumns = getGoodsConsumColumns(changeConsumeQty, isResult);
  const pheripheralReturnColumns = getPheripheralReturnColumns(
    changePeripheralConsumeQty,
    isResult || $.type === CheckType.CheckoutCheckRoom, // 外设 退房查房 外设数量不可编辑
  );

  const { totalNum, totalPrice } = calGoodsTotalConsum($.goodsList);

  // 确认查房完毕
  const confirmEndInventory = async () => {
    dispatch({ type: 'update-state', payload: { pageLoading: true } });
    try {
      const { code } = await saveInventoryResult({
        checkStatus: 2,
        computerList: $.computerList,
        goodsList: $.goodsList,
        peripheralList: $.peripheralList,
        id: $$.id,
        resvNo: $$.resvNo,
      });

      if (code === 0 && totalNum === 0) {
        $$.callback();
        message.success(intl.get('cmp_inventory_message_inventory_success'));
      } else if (code === 0 && totalNum !== 0) {
        $$.callback();
        message.success(intl.get('cmp_inventory_message_bill_success'));
      }
      $$.unmountNode();
      dispatch({ type: 'update-state', payload: { pageLoading: false } });
    } catch (error) {
      dispatch({ type: 'update-state', payload: { pageLoading: false } });
      // 如果已在移动端提交
      const { code } = error;
      if (code === 51005) {
        $$.callback();
        $$.unmountNode();
      }
    }
  };

  if (!modalState.isInit) return <PortalLoading />;

  return (
    <Spin spinning={modalState.isLoading || $.pageLoading}>
      <DrawerHeader
        title={
          isResult
            ? intl.get('cmp_inventory_title_result')
            : intl.get('cmp_inventory_title_inventory')
        }
        onClose={$$.unmountNode}
      />
      <section className={styles.container}>
        <div className={styles.roomInfos}>
          <div className={styles.title}>{intl.get('cmp_inventory_room_block_title')}</div>
          <div className={styles.infos}>
            <div>
              <span>{intl.get('cmp_inventory_room_inventory_type')}</span>
              {getTypeText($.type)}
            </div>
            <div>
              <span>{intl.get('cmp_inventory_room_inventory_no')}</span>
              {$.roomNo || '--'}
            </div>
            <div>
              <span>{intl.get('cmp_inventory_room_inventory_bill')}</span>
              {$.billRoomNo || '--'}
            </div>
          </div>
        </div>
        <div className={styles.computerCheck}>
          <div className={styles.title}>{intl.get('cmp_inventory_computer_block_title')}</div>
          <div className={styles.computerCheckTableWrapper}>
            <Table
              // style={{ width: 871 }}
              style={{ width: 952 }}
              rowKey={(record) => `${record.computerId}`}
              pagination={false}
              columns={computerColumns || []}
              scroll={{ y: 250 }}
              dataSource={$.computerList || []}
            />
          </div>
        </div>
        <div className={styles.goodsConsum}>
          <div className={styles.title}>{intl.get('cmp_inventory_consume_block_title')}</div>
          {!isResult && (
            <div className={styles.searchWrapper}>
              <Input
                value={$.searchText}
                placeholder={intl.get(intl.get('cmp_inventory_consume_search_name_placeholder'))}
                prefix={<SearchOutlined />}
                style={{ width: '30%' }}
                onChange={onInput}
              />
              <Button type="primary" className={styles.btn} onClick={searchHandle}>
                {intl.get('app_search_form_search_btn')}
              </Button>
              <Button type="default" className={styles.btn} onClick={resetHandle}>
                {intl.get('app_search_form_reset_btn')}
              </Button>
            </div>
          )}
          <div className={styles.goodsConsumTableWrapper}>
            <Table
              // style={{ width: 871 }}
              style={{ width: 952 }}
              rowKey={(record) => `${record.skuCode}`}
              columns={goodsConsumColumns}
              dataSource={$.searching ? $.goodsListCopy : $.goodsList || []}
              pagination={false}
              scroll={{ y: 450 }}
            />
          </div>
          <div className={styles.title}>外设归还</div>
          <div className={styles.goodsConsumTableWrapper}>
            <Table
              // style={{ width: 871 }}
              style={{ width: 952 }}
              rowKey={(record) => `${record.id}`}
              columns={pheripheralReturnColumns}
              dataSource={$.peripheralList || []}
              pagination={false}
              scroll={{ y: 450 }}
              sticky
            />
          </div>
          <div className={styles.total}>
            {intl.getHTML('cmp_inventory_consume_total', {
              total: totalNum,
              price: parseYuan2Currency(totalPrice || 0),
            })}
          </div>
        </div>
      </section>
      <DrawerFooter>
        {!isResult && totalNum === 0 && (
          <Popconfirm
            overlayStyle={{ width: 294 }}
            overlayClassName={styles.popConfirm}
            placement="topRight"
            title={intl.get('cmp_inventory_submit_cofirm')}
            onConfirm={confirmEndInventory}
          >
            <Button type="primary">{intl.get('cmp_inventory_submit_btn')}</Button>
          </Popconfirm>
        )}
        {!isResult && totalNum !== 0 && (
          <Button type="primary" onClick={confirmEndInventory}>
            {intl.get('cmp_inventory_submit_btn')}
          </Button>
        )}
        {!isResult && (
          <Button onClick={$$.unmountNode}>{intl.get('app_confirm_cancel_btn')}</Button>
        )}
        {isResult && (
          <Button type="primary" onClick={$$.unmountNode}>
            {intl.get('app_confirm_close_btn')}
          </Button>
        )}
      </DrawerFooter>
    </Spin>
  );
};

export default () => usePortalDrawer<IConfig>(Index);
